import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTrial } from '../contexts/TrialContext';
import api from '../lib/api';
import { startProCheckout } from '../lib/checkout';
import { track } from '../lib/analytics';
import { X, Crown, Check, Copy, Gift, Loader2, ArrowRight } from 'lucide-react';

const FEATURES = [
  'All full mock tests — exact CELPIP replica',
  'AI scoring on Writing & Speaking in seconds',
  'Every course, video lesson & strategy unlocked',
  'Live group classes + 1:1 teacher support',
];

/**
 * Global Pro upgrade window. Opened from anywhere via the `cmx:open-upgrade`
 * window event (dispatched by goPro in lib/checkout.js). Free users who click
 * Pro content see the pitch first; "Maybe later" AND the close (X) button
 * both reveal the downsell offer (dynamic coupon + % from /admin/promotions)
 * with a live countdown + spots-left scarcity.
 */
export default function UpgradeModal() {
  const { user } = useAuth();
  const { trialMode } = useTrial();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('pitch');
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [spots, setSpots] = useState(null);

  // Pre-fetch the promo config at mount so the downsell decision (laterEnabled)
  // and the popup on/off switch are ready even if the user clicks immediately.
  useEffect(() => {
    api.get('/api/promo/config').then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []);

  useEffect(() => {
    const onOpen = () => {
      if (cfg && cfg.pitch_enabled === false) {
        // Admin turned the popup off → every Upgrade CTA goes straight to checkout.
        track('upgrade_modal_bypassed');
        if (!user) { navigate('/signup?intent=pro'); return; }
        startProCheckout(Number(cfg.price || 0));
        return;
      }
      setStep('pitch'); setBusy(false); setOpen(true); track('upgrade_modal_shown');
    };
    window.addEventListener('cmx:open-upgrade', onOpen);
    return () => window.removeEventListener('cmx:open-upgrade', onOpen);
  }, [cfg, user, navigate]);

  // Downsell countdown + live spots (shared session keys with the exit-intent
  // modal so the scarcity stays consistent across both popups)
  useEffect(() => {
    if (!open || step !== 'offer' || !cfg) return undefined;
    let deadline = Number(sessionStorage.getItem('cmx_exit_deadline') || 0);
    if (!deadline || deadline < Date.now()) {
      deadline = Date.now() + Number(cfg.exit_timer_minutes || 10) * 60000;
      sessionStorage.setItem('cmx_exit_deadline', String(deadline));
    }
    const tick = () => setSecondsLeft(Math.max(0, Math.floor((deadline - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    const start = Number(sessionStorage.getItem('cmx_exit_spots') || cfg.exit_spots || 5);
    setSpots(start);
    const st = setTimeout(() => {
      const next = Math.max(1, start - 1);
      setSpots(next);
      sessionStorage.setItem('cmx_exit_spots', String(next));
    }, 9000);
    return () => { clearInterval(id); clearTimeout(st); };
  }, [open, step, cfg]);

  if (!open) return null;

  const price = Number(cfg?.price || 149);
  const symbol = cfg?.currency_symbol || '$';
  const pct = Number(cfg?.later_discount_pct || 0);
  const code = cfg?.later_coupon_code || '';
  const laterEnabled = cfg === null || (cfg?.later_enabled !== false && pct > 0 && !!code);
  const discounted = (price * (1 - pct / 100)).toFixed(2);
  const mm = String(Math.floor((secondsLeft ?? 0) / 60)).padStart(2, '0');
  const ss = String((secondsLeft ?? 0) % 60).padStart(2, '0');
  const valueText = cfg?.exit_value_text || `${symbol}${discounted} once is cheaper than failing the real test again.`;

  const checkout = async (couponCode) => {
    if (!user) { setOpen(false); navigate('/signup?intent=pro'); return; }
    setBusy(true);
    try {
      const url = await startProCheckout(price, couponCode);
      if (url) return;
    } catch { /* checkout unavailable */ }
    setBusy(false);
    setOpen(false);
    navigate('/pricing');
  };

  const copyCode = () => {
    try {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard unavailable */ }
  };

  // Both "Maybe later" and the X close the pitch INTO the downsell offer.
  const dismissPitch = () => {
    if (laterEnabled) { setStep('offer'); track('upgrade_downsell_shown'); }
    else setOpen(false);
  };
  const handleClose = () => {
    if (step === 'pitch') dismissPitch();
    else setOpen(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 120 }} onClick={handleClose} data-testid="upgrade-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 sm:p-8 relative max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={handleClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-500 hover:bg-neutral-200 transition" data-testid="upgrade-modal-close">
          <X size={16} />
        </button>

        {step === 'pitch' ? (
          <div data-testid="upgrade-pitch-step">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mb-4">
              <Crown size={24} />
            </div>
            <h2 className="text-2xl font-extrabold text-neutral-900 mb-1" data-testid="upgrade-pitch-title">{cfg?.pitch_title || 'Unlock CELPIPMax Pro'}</h2>
            <p className="text-sm text-neutral-500 mb-5" data-testid="upgrade-pitch-subtitle">{cfg?.pitch_subtitle || 'This is a Pro feature. Upgrade to get everything you need to pass on your next attempt.'}</p>
            <div className="flex items-baseline gap-2 mb-5">
              <span className="text-4xl font-extrabold text-neutral-900" data-testid="upgrade-price">{symbol}{price}</span>
              <span className="text-neutral-400 text-sm">one-time · 3 months of full access</span>
            </div>
            <ul className="space-y-2.5 mb-6">
              {(Array.isArray(cfg?.pitch_features) && cfg.pitch_features.filter(Boolean).length ? cfg.pitch_features.filter(Boolean) : FEATURES).map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-neutral-700">
                  <Check size={16} className="text-emerald-500 mt-0.5 shrink-0" />{f}
                </li>
              ))}
            </ul>
            <button onClick={() => checkout()} disabled={busy}
              className="w-full py-3.5 rounded-xl bg-blue-600 text-white font-bold text-base hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="upgrade-now-btn">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <>{cfg?.pitch_cta_label || 'Upgrade Now'} <ArrowRight size={16} /></>}
            </button>
            <button onClick={dismissPitch}
              className="w-full mt-2.5 py-2.5 rounded-xl text-sm font-medium text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50 transition"
              data-testid="maybe-later-btn">
              {trialMode ? 'Not now — keep exploring my trial' : 'Maybe later'}
            </button>
          </div>
        ) : (
          <div data-testid="maybe-later-offer">
            <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
              <Gift size={24} />
            </div>
            <h2 className="text-2xl font-extrabold text-neutral-900 mb-1">{cfg?.later_title || "Wait — here's a one-time deal"}</h2>
            <p className="text-sm text-neutral-500 mb-4">{cfg?.later_text || 'Grab this discount on your 3-month pass.'}</p>

            {/* Countdown */}
            <div className="rounded-2xl bg-slate-950 text-center py-3 mb-3">
              <p className="text-white text-[11px] font-bold tracking-widest uppercase mb-0.5">{pct}% discount ends in</p>
              <p className="text-red-500 font-mono text-3xl font-extrabold tabular-nums tracking-wider" data-testid="downsell-timer">{mm}:{ss}</p>
            </div>

            <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 p-4 text-center mb-4">
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="text-lg font-bold text-rose-400 line-through">{symbol}{price}</span>
                <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-600 text-xs font-bold">-{pct}%</span>
              </div>
              <div className="text-3xl font-extrabold text-neutral-900" data-testid="maybe-later-price">{symbol}{discounted}<span className="text-base font-semibold text-neutral-500"> one-time</span></div>
              <div className="flex items-center justify-center gap-2 mt-1 text-xs text-neutral-500">
                <span>one payment · 3 months of Pro access</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-500 font-semibold" data-testid="downsell-spots-left">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />Only {spots ?? cfg?.exit_spots ?? 5} spots left
                </span>
              </div>
            </div>

            <p className="text-[11px] font-bold tracking-widest uppercase text-neutral-400 text-center mb-2">Use code at checkout</p>
            <div className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50/50 mb-4" data-testid="maybe-later-coupon">
              <span className="font-mono font-extrabold text-lg tracking-widest text-blue-700" data-testid="maybe-later-coupon-code">{code}</span>
              <button onClick={copyCode} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-blue-100 transition" data-testid="maybe-later-copy-btn">
                {copied ? <Check size={15} className="text-emerald-600" /> : <Copy size={15} className="text-blue-400" />}
                <span className="text-xs font-semibold text-blue-500">{copied ? 'Copied!' : 'Copy'}</span>
              </button>
            </div>

            <button onClick={() => { copyCode(); checkout(code); }} disabled={busy}
              className="w-full py-3.5 rounded-xl bg-emerald-600 text-white font-bold text-base hover:bg-emerald-700 transition flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="maybe-later-claim-btn">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <>Claim {pct}% Off <ArrowRight size={16} /></>}
            </button>
            <p className="text-xs text-center text-blue-700 font-medium mt-3 mb-1" data-testid="downsell-subtext">
              <span role="img" aria-label="idea">💡</span> {valueText}
            </p>
            <button onClick={() => setOpen(false)}
              className="w-full mt-1.5 py-2.5 rounded-xl text-sm font-medium text-neutral-400 hover:text-neutral-600 transition"
              data-testid="maybe-later-dismiss-btn">
              {trialMode ? 'No thanks, keep exploring my trial' : 'No thanks, keep exploring free'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
